import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ShoppingCart } from 'lucide-react'

import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FeatureGate } from '@/components/feature-gate'
import { KiotVietOrdersTable } from '@/components/kiotviet/kiotviet-orders-table'
import { useAuth } from '@/hooks/use-auth'
import { useKiotVietOrders } from '@/hooks/use-kiotviet-orders-query'
import { m } from '@/lib/paraglide/messages'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Trước đây cả ba nằm trong `useState` nên không vào URL, và ba ô đều **không
 * có nhãn** — chỉ có placeholder cho ô tìm kiếm, hai ô ngày thì trống trơn (R7).
 */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.kiotviet_orders_search_label,
        placeholder: m.kiotviet_orders_search_placeholder,
    }),
    from: dateFilter({ label: m.report_from_date }),
    to: dateFilter({ label: m.report_to_date }),
})

export const Route = createFileRoute('/_app/_authed/_sidebar/kiotviet-orders')({
    component: GatedKiotVietOrdersPage,
    validateSearch: FILTERS.validateSearch,
})

// The order mirror is unpaid customer scope: the flag gate blocks direct URL
// access too, not just the hidden sidebar entry. Flip dev-kiotviet-orders in
// PostHog once the customer approves the quote.
function GatedKiotVietOrdersPage() {
    return (
        <FeatureGate
            flag="dev-kiotviet-orders"
            fallback={
                <p className="text-sm text-muted-foreground">
                    {m.kiotviet_orders_unavailable()}
                </p>
            }
        >
            <KiotVietOrdersPage />
        </FeatureGate>
    )
}

const PER_PAGE = 20

function KiotVietOrdersPage() {
    const { hasPermission } = useAuth()
    const navigate = useNavigate({ from: '/kiotviet-orders' })
    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 4,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const { data, isLoading } = useKiotVietOrders({
        page,
        per_page: PER_PAGE,
        search: values.q || undefined,
        from: values.from || undefined,
        to: values.to || undefined,
    })

    if (!hasPermission('sales_orders.read')) {
        return (
            <div className="p-8 text-center text-destructive">
                {m.common_permission_denied()}
            </div>
        )
    }

    const items = data?.items ?? []
    const total = data?.total ?? 0
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))
    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<ShoppingCart className="size-5 shrink-0" />}
                title={m.kiotviet_orders_title()}
            />
            <p className="text-sm text-muted-foreground">
                {m.kiotviet_orders_subtitle()}
            </p>

            <ListFilterBar {...barProps} />

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">
                        {m.kiotviet_orders_title()} ({total})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="p-8 text-center text-muted-foreground">
                            {m.common_loading()}
                        </div>
                    ) : (
                        <KiotVietOrdersTable orders={items} />
                    )}
                </CardContent>
            </Card>

            {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                    >
                        ←
                    </Button>
                    <span className="text-sm text-muted-foreground">
                        {page}/{totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage(page + 1)}
                    >
                        →
                    </Button>
                </div>
            )}
        </div>
    )
}
